<template lang="pug">
    include ../../icon/arrow
    include ../../icon/ibkr

    div(ref="_in",
        class="_pdate _con",
        :class="_class",
        :style="{ width, height }",
        :id="id",
        :inert="inert || disabled",
        :tabindex="inert || disabled ? null : tabindex",
        @keypress="keypress",
        @wheel="_wheel",
        v-on="__on")

        span(class="_top")
            a(role="button", class="_cap fg", @click="zoomOut", v-html="heading")
            span(class="_dir flex-fixed")
                a(class="_blast fg50 mirror-rtl", role="button", @mouseup.left="last")
                    +icon-arrow-back
                a(role="button", @mouseup.left="today", v-label="$locale.data.Today")
                    +icon-ibkr-today
                a(class="_bnext fg50 mirror-rtl", role="button", @mouseup.left="next")
                    +icon-arrow-forward

        table(class="_pg _cal", role="grid", :class="{_hide: page != 0}", @mouseover="hover", @mouseleave="hidesel", @mouseup.left="select")
            tr(class="fg50")
                th(v-for="day in lday", class="_h _d") {{day}}
            tr(v-for="row in 6", role="row")
                td(v-for="col in 7",
                    role="gridcell",
                    :class="cday[row*7+col-8]._c",
                    :aria-selected="cday[row*7+col-8]._s",
                    :data-val="cday[row*7+col-8].value")
                    slot(v-bind="cday[row*7+col-8]")
                        | {{ cday[row*7+col-8].label }}

        table(class="_pg _mon", role="grid", :class="{_hide: page != 1}", @mouseover="hover", @mouseleave="hidesel", @mouseup.left="select")
            tr(v-for="row in 3", role="row")
                td(v-for="col in 4",
                    role="gridcell",
                    :class="cmonth[row*4+col-5].c",
                    :data-val="cmonth[row*4+col-5].v")
                    | {{ lmonth[row*4+col-5] }}

        table(class="_pg _yr", role="grid", :class="{_hide: page != 2}", @mouseover="hover", @mouseleave="hidesel", @mouseup.left="select")
            tr(v-for="row in 3", role="row")
                td(v-for="col in 4",
                    role="gridcell",
                    class="_d",
                    :class="cyear[row*4+col-5].c",
                    :data-val="cyear[row*4+col-5].v")
                    | {{ cyear[row*4+col-5].l }}
</template>

<script>
    import entry from './ib-form-entry.vue';
    import telemetry from '../private/telemetry';
    import $date from '../../prototype/date';

    const useHover = $build.platform != 'ios' && $build.platform != 'android';

    const days = 0;
    const months = 1;
    const years = 2;

    const skip = [  // skip using heading arrows
        [1, months],
        [1, years],
        [10, years]
    ];
    const skiph = [ // skip using left/right keys
        [1, days],
        [1, months],
        [1, years]
    ];
    const skipv = [ // skip using up/down keys
        [7, days],
        [4, months],
        [4, years]
    ];

    const pad = n => n < 10 ? '0' + n : n;
    const clamp = (value, min, max) => value < min || value > max ? null : value;
    const transDuration = 120;

    export default {
        name: 'ib-pick-date',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
             * The selected date in ISO format; generally use v-model
             */
            value: {
                type: String,
                default: undefined
            },
            /**
             * The minimum date in ISO format: YYYY-MM-DD or ISO duration format for offset from now
             */
            min: {
                type: String,
                default: '1900-01-01'
            },
            /**
             * The maximum date in ISO format: YYYY-MM-DD or ISO duration format for offset from now
             */
            max: {
                type: String,
                default: '2099-12-31'
            },
            /**
             * If true, allow the calendar to be panned horizontally using infinite scrolling or buttons at edges.
             */
            pan: Boolean,
            /**
             * Appearance modifier: `frameless` is supported.
             */
            appearance: String,
            /**
             * If `true`, accept mouse wheel panning.
             */
            wheel: Boolean
        },

        data() {
            return {
                now: $date.local().startOfDay(),
                act: null,
                sel: null,
                seltd: null,
                selvisible: false,
                base: null,
                page: days,
                trans: null
            };
        },

        computed: {
            _class() {
                return {
                    _fo: this.focused,
                    _da: this.disabled,
                    _pan: this.pan,
                    ['_s' + this.$locale.date.weekStart]: true,
                    ['_e' + this.appearance]: this.appearance
                };
            },

            lday() {
                let fmt = $date.format({ weekday: 'narrow', timeZone: 'UTC' });
                let cur = $date.utc('1973');   // 1/1/1973 is a Monday; Safari has arithmetic bugs using local time
                cur.day += this.$locale.date.weekStart;
                let r = [];
                for (let i = 0; i < 7; i++) {
                    r[i] = fmt(cur);
                    cur.day++;
                }
                return r;
            },

            lmonth() {
                let fmt = $date.format({ month: 'short', timeZone: 'UTC' });
                let cur = $date.utc('1973');
                let r = [];
                for (let i = 0; i < 12; i++) {
                    r[i] = fmt(cur);
                    cur.month++;
                }
                return r;
            },

            minDate() {
                const r = /^[\-+]/.test(this.min)
                    ? this.now.clone().add(this.min)
                    : $date.local(this.min);
                if (!r.isValid()) {
                    console.error('ib-pick-date: invalid format for min');
                }
                return r;
            },

            maxDate() {
                const r = /^[\-+]/.test(this.max)
                    ? this.now.clone().add(this.max)
                    : $date.local(this.max);
                if (!r.isValid()) {
                    console.error('ib-pick-date: invalid format for max');
                }
                return r;
            },

            minStr() {
                return this.minDate.toDateString();
            },

            maxStr() {
                return this.maxDate.toDateString();
            },

            heading() {
                const year = this.base.year;
                switch (this.page) {
                case days:
                    return '<b>' + $date.mmmm(this.base) + '</b>&nbsp;' + year;
                case months:
                    return '<b>' + year + '</b>';
                case years:
                    return '<b>' + (year - year % 10) + '-' + (year - year % 10 + 9) + '</b>';
                }
            },

            cday() {
                let r = new Array(6 * 7);

                const base = this.base;
                const start = ((base.getDayOfWeek() + 7 - this.$locale.date.weekStart) % 7) || 7;
                const today = start + this.now.diffDays(base);

                let active = this.act.day;
                if (!base.isEqual(this.act.clone().startOfMonth()))
                    active = -1;

                let ind = 0;
                let day = 1;
                let row = 0;
                let date, cnt, pre;

                date = base.clone();
                date.day = 0;
                cnt = date.day;   // number of days in last month
                pre = date.year + '-' + pad(date.month + 1) + '-';

                for (let i = start; i > 0; i--) {
                    r[ind++] = {
                        label: cnt - i + 1,
                        value: clamp(pre + pad(cnt - i + 1), this.minStr, this.maxStr),
                        _c: '_d _opq',
                        _s: false
                    };
                }

                date = base.clone();
                date.month++;
                date.day = 0;
                cnt = date.day;
                pre = date.year + '-' + pad(date.month + 1) + '-';

                let cls = '_d';
                while (ind < 6 * 7) {
                    r[ind++] = {
                        label: day,
                        value: clamp(pre + pad(day), this.minStr, this.maxStr),
                        _c: cls,
                        _s: false
                    };
                    if (++day > cnt) {
                        day = 1;
                        cls = '_d _opq';
                        date = base.clone();
                        date.month++;
                        pre = date.year + '-' + pad(date.month + 1) + '-';
                    }
                }

                if (active >= 0) {
                    r[start + active - 1]._s = true;
                }
                if (today >= 0 && today < 6 * 7) {
                    r[today]._c += ' _td';
                }
                return r;
            },

            cmonth() {
                let r = new Array(3 * 4);
                const pre = this.base.year + '-';
                const min = this.minStr.slice(0,7);
                for (let i = 0; i < 12; i++) {
                    const val = pre + pad(i + 1);
                    r[i] = {
                        v: val < min ? null : val,
                        c: '_d'
                    };
                }
                if (this.now.year == this.base.year) {
                    r[this.now.month].c += ' _td';
                }
                return r;
            },

            cyear() {
                let r = new Array(3 * 4);
                const pre = this.base.year - this.base.year % 10 - 1;
                const min = this.minStr.slice(0,4);
                for (let i = 0; i < 12; i++) {
                    r[i] = {
                        v: pre + i < min ? null : pre + i,
                        l: pre + i,
                        c: pre + i < min ? '_opq' : null
                    };
                }
                if (pre == this.now.year - this.now.year % 10 - 1) {
                    r[this.now.year % 10 + 1].c += ' _td';
                }
                return r;
            }
        },

        created() {
            this.setact();
        },

        methods: {
            go(when) {
                this._set(/^[\-+]/.test(when)
                    ? this.act.clone().add(when).toDateString()
                    : when);
            },

            setact() {
                if (this.ivalue) {
                    this.act = $date.local(this.ivalue);
                }
                if (!this.ivalue || !this.act.isValid()) {
                    this.act = this.now.clone();
                }
                this.sel = this.act.clone();
                this.setbase(this.act.clone().startOfMonth());
            },

            /** @private */
            _sync() {
                if (this.dirty) {
                    this.setact();
                    this.$nextTick(() => {
                        this.setsel(this.$el.querySelector('[aria-selected=true]'), document.activeElement === this.$el);
                        this.validate();
                        this.dirty = false;
                    });
                }
            },

            setsel(td, show) {
                this.hidesel();
                if (this.sel && td) {
                    this.seltd = td;
                    if (show !== false) {
                        this.showsel();
                    }
                }
            },

            setbase(base) {
                const min = this.minDate.clone().startOfMonth();
                if (base < min) {
                    this.base = min;
                } else {
                    const max = this.maxDate.clone().startOfMonth();
                    if (base > max) {
                        this.base = max;
                    } else {
                        this.base = base;
                    }
                }
            },

            add(inc, unit) {
                if (inc) {
                    switch (unit) {
                    case days:
                        this.sel.day += inc;
                        break;
                    case months:
                        this.sel = this.base.clone();
                        this.sel.month += inc;
                        break;
                    case years:
                        this.sel = this.base.clone();
                        this.sel.year += inc;
                        break;
                    }
                }
                if (this.sel < this.minDate) {
                    this.sel = this.minDate.clone();
                }
                if (this.sel > this.maxDate) {
                    this.sel = this.maxDate.clone();
                }
                const base = this.sel.clone().startOfMonth();
                if (!this.base.isEqual(base)) {  // avoid rerender
                    this.setbase(base);
                }
            },

            last() {
                const add = skip[this.page];
                this.hidesel();
                this.seltd = null;
                this.add(-add[0], add[1]);
            },

            today() {
                const value = this.now.toDateString();
                if (this.ivalue != value) {
                    this._set(value);
                } else {
                    this.setbase(this.now.clone().startOfMonth());
                }
                this.setPage(days);
            },

            next() {
                const add = skip[this.page];
                this.hidesel();
                this.seltd = null;
                this.add(add[0], add[1]);
            },

            keypress(e) {
                if (useHover && !this.inert && !this.disabled) {
                    const page = this.page;
                    if (page != years && e.ctrlKey) {  // change year
                        switch (e.keyCode) {
                        case 38:      // up
                        case 37:      // left
                            this.add(-1, years);
                            break;
                        case 40:      // down
                        case 39:      // right
                            this.add(1, years);
                            break;
                        default:
                            return;
                        }
                    } else {
                        switch (e.keyCode) {
                        case 38:      // up
                            this.add(-skipv[page][0], skipv[page][1]);
                            break;
                        case 40:      // down
                            this.add(skipv[page][0], skipv[page][1]);
                            break;
                        case 37:      // left
                            this.add(-skiph[page][0], skiph[page][1]);
                            break;
                        case 39:      // right
                            this.add(skiph[page][0], skiph[page][1]);
                            break;
                        case 13:      // enter
                            this.click(this.sel.toDateString());
                            break;
                        default:
                            return;
                        }
                    }
                    this.$nextTick(() => {
                        this.setsel(this.$el.querySelector('td[data-val="' + this.sel.toDateString().slice(0,[10,7,4][this.page]) + '"]'));
                    });
                    e.preventDefault();
                }
            },

            click(value) {
                switch (this.page) {
                case days:
                    this._set(value);
                    break;
                case months:
                    this.setPage(days);
                    this.sel = $date.local(value);
                    this.add();
                    break;
                case years:
                    this.setPage(months);
                    this.sel = $date.local(value);
                    this.add();
                    break;
                }
            },

            hover(e) {
                if (useHover && !this.trans) {
                    let target = e.target;
                    while (target) {
                        if (target.tagName == 'TD') {
                            const value = target.getAttribute('data-val');
                            if (value) {
                                this.sel = $date.local(value);
                                this.setsel(target);
                            } else {
                                this.hidesel();
                            }
                            return;
                        }
                        target = target.parentElement;
                    }
                    this.hidesel();
                }
            },

            /** @private */
            _focus() {
                if (!this.selvisible) {
                    const td = this.$el.querySelector('[aria-selected=true]');
                    if (td) {
                        this.sel = $date.local(td.getAttribute('data-val'));
                        this.setsel(td);
                    } else {
                        this.setsel(this.seltd);
                    }
                }
            },

            /** @private */
            _blur() {
                this.hidesel();
            },

            showsel() {
                if (useHover && this.seltd && !this.selvisible) {
                    this.selvisible = true;
                    this.seltd.classList.add('_foc');
                }
            },

            hidesel() {
                if (useHover && this.seltd && this.selvisible) {
                    this.selvisible = false;
                    this.seltd.classList.remove('_foc');
                }
            },

            select(e) {
                if (e.button == 0 && !this.readonly) {
                    let target = e.target;
                    while (target) {
                        if (target.tagName == 'TD') {
                            const value = target.getAttribute('data-val');
                            if (value) {
                                this.click(value);
                            }
                            return;
                        }
                        target = target.parentElement;
                    }
                }
            },

            zoomOut() {
                if (this.page < years) {
                    this.setPage(this.page + 1);
                }
            },

            setPage(page) {
                this.page = page;
                if (this.trans) {
                    clearTimeout(this.trans);
                }
                this.trans = setTimeout(() => {
                    this.trans = null;
                }, transDuration);
            },

            _wheel(e) {
                if (this.wheel) {
                    if (e.deltaY < 0) {
                        this.last();
                    } else if (e.deltaY > 0) {
                        this.next();
                    }
                    e.stopPropagation();
                    e.preventDefault();
                }
            },

            validate() {
                return true;
            }
        }
    };
</script>

<style lang="less">
    @import "../../less/globals.less";

    @compact-width: 34px;
    @compact-height: 28px;
    @compact-mwidth: 56px;
    @compact-mheight: 56px;
    @default-width: 38px;
    @default-height: 34px;
    @default-mwidth: 64px;
    @default-mheight: 64px;

    ._pdate {
        display: flex;
        flex-direction: column;
        position: relative;
        white-space: normal;
        overflow: hidden;
        height: auto;
        text-align: center;
        background-color: @bg-field;
        padding: 4px;

        &._da {
            opacity: .5;
        }
        &._pan {
            padding: 4px 32px;
            ._blast,
            ._bnext {
                position: absolute;
                margin: 0;
                top: calc(~'50% - 10px');
            }
            ._blast {
                left: 0;
            }
            ._bnext {
                right: 0;
            }
        }

        ._top {
            display: flex;
            justify-content: space-between;
            padding: 6px 4px 8px;
            width: 100%;
            line-height: 30px;
            margin: 0 auto;
            &:before {
                content: '\2002';
            }
        }
        ._cap {
            display: inline-flex;
            flex: 1 1 auto;
            .font-size(@fs5);
            overflow: hidden;
            > b {
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
        ._blast,
        ._bnext {
            padding: 0 6px;
        }

        ._top {
            max-width: @default-width * 7;
        }
        ._pg {
            margin: 0 auto;
            table-layout: fixed;
            border-collapse: collapse;
            border-spacing: 0;
            text-align: center;
            transform: scale(1);
            transition: opacity .12s linear, transform .12s linear;
            -webkit-tap-highlight-color: transparent;
        }

        ._d {
            position: relative;
            padding: 0;
            font-size: 13px;
            opacity: .4;
            &[data-val] {
                cursor: pointer;
                opacity: 1;
            }
            &._opq {
                opacity: .4;
            }
            &::after {
                position: absolute;
                border-radius: 50%;
                left: 0;
                right: 0;
                z-index: -1;
            }
            &._td {
                font-weight: 700;
                &::after {
                    content: '';
                    border: 1px solid @fg-accent;
                    z-index: -2;
                }
            }
            &[aria-selected=true] {
                color: @bg;
                font-weight: 700;
                &::after {
                    content: '';
                    background: @fg-max;
                }
            }
            &._foc when (@have-hover) {
                color: @fg-select;
                &::after {
                    content: '';
                    background: @bg-select;
                }
                &[aria-selected=true] {
                    color: @bg;
                    &::after {
                        background: mix(@bg-accent, @fg-max, 50%);
                    }
                }
            }
        }
        ._h {
            padding: 10px 0 6px;
            font-size: 11px;
            font-weight: 400;
        }

        ._cal {
            ._d,
            ._h {
                width: @default-width;
                &::after {
                    top: (@default-height - @default-width) / 2;
                    bottom: (@default-height - @default-width) / 2;
                }
            }
            ._d {
                height: @default-height;
            }
            ._h {
                height: auto;
            }
            tr:last-child > ._d {
                height: @default-height + 2px;
                padding-bottom: 2px;
            }
        }
        ._mon,
        ._yr {
            position: absolute;
            top: calc(~'50% - 80px');
            left: 0;
            right: 0;
            max-width: @default-mwidth * 4;
            ._d {
                width: @default-mwidth;
                height: @default-mheight;
                &::after {
                    top: (@default-mheight - @default-mwidth) / 2;
                    bottom: (@default-mheight - @default-mwidth) / 2;
                }
            }
        }

        ._hide {
            visibility: hidden;
            opacity: 0;
            transform: scale(1.5);
            transition: visibility 0s linear .12s, opacity .12s linear, transform .12s linear;
        }
        &._inst ._pg {
            transition: none;
        }
        &.compact {
            ._top {
                padding: 2px 0;
            }
            ._cap {
                .font-size(@fs6);
            }
            ._dir {
                .font-size(@fs7);
            }
            ._top {
                max-width: @compact-width * 7;
            }
            ._d {
                &::after {
                    top: (@compact-height - @compact-width) / 2;
                    bottom: (@compact-height - @compact-width) / 2;
                }
            }
            ._h {
                padding: 10px 0 5px;
            }
            ._cal {
                ._d,
                ._h {
                    width: @compact-width;
                }
                ._d {
                    height: @compact-height;
                    &::after {
                        top: (@compact-height - @compact-width) / 2;
                        bottom: (@compact-height - @compact-width) / 2;
                    }
                }
                ._h {
                    height: auto;
                }
                tr:last-child > ._d {
                    height: @compact-height + 2px;
                    padding-bottom: 2px;
                }
            }
            ._mon,
            ._yr {
                top: calc(~'50% - 72px');
                line-height: 58px;
                max-width: @compact-mwidth * 4;
                ._d {
                    width: @compact-mwidth;
                    height: @compact-mheight;
                    &::after {
                        top: (@compact-mheight - @compact-mwidth) / 2;
                        bottom: (@compact-mheight - @compact-mwidth) / 2;
                    }
                }
            }
        }

        /* Weekend styling */
        &._s0 ._cal ._d:nth-child(6),
        &._s0 ._cal ._d:last-child,
        &._s5 ._cal ._d:first-child,
        &._s5 ._cal ._d:nth-child(2),
        &._s6 ._cal ._d:first-child,
        &._s6 ._cal ._d:last-child {
            opacity: .6;
            background: fade(@fg-max, 6.25%);
            &._opq {
                opacity: .4;
                background: fade(@fg-max, 9.375%);
            }
        }
    }

    /** Overlay */
    ._mdlsdef._pdate {
        background-color: inherit;
    }
</style>

<docs>
    Examples:
    ```jsx
        new Vue({
            data() {
                return {
                    value: null
                }
            },
            template:
                `<ib-context>
                    <p class="after-md">Basic Example: {{value}}</p>
                    <ib-pick-date v-model="value"/>
                    <p class="before-lg after-md">Panning: {{value}}</p>
                    <ib-pick-date v-model="value" pan/>
                    <p class="before-lg after-md">Compact: {{value}}</p>
                    <ib-pick-date v-model="value" class="compact"/>
                    <p class="before-lg after-md">Compact, Disabled: {{value}}</p>
                    <ib-pick-date v-model="value" class="compact" disabled/>
                </ib-context>`
        })
    ```
</docs>
